import React from 'react';
import ReactImageMagnify from 'react-image-magnify';
import './ImageMagnify.css';
import PropTypes from 'prop-types';

/**
 * A reusable component for image magnification.
 *
 * @param {Object} props - The props for the component.
 * @param {string} props.data_url - The source URL of the image.
 * @param {string} [props.imageClassName] - CSS class for the small image.
 * @param {Object} [props.smallImage] - Configuration for the small image.
 * @param {string} [props.smallImage.alt] - Alt text for the small image.
 * @param {boolean} [props.smallImage.isFluidWidth] - Whether the small image has fluid width.
 * @param {string} [props.smallImage.src] - Source URL for the small image.
 * @param {number} [props.smallImage.width] - Width of the small image.
 * @param {number} [props.smallImage.height] - Height of the small image.
 * @param {Object} [props.largeImage] - Configuration for the large image.
 * @param {string} [props.largeImage.src] - Source URL for the large image.
 * @param {number} [props.largeImage.width] - Width of the large image.
 * @param {number} [props.largeImage.height] - Height of the large image.
 * @param {string} [props.enlargedImageClassName] - CSS class for the enlarged image.
 * @param {Object} [props.enlargedImageContainerDimensions] - Dimensions for the enlarged image container.
 * @param {string} [props.enlargedImageContainerDimensions.width] - Width of the enlarged image container.
 * @param {string} [props.enlargedImageContainerDimensions.height] - Height of the enlarged image container.
 */

const ImageMagnify = ({ data_url = '', ...props }) => {
    const { smallImage, largeImage } = props;
    const defaultProps = {
        imageClassName: 'small_image',
        smallImage: {
            alt: 'Uploaded Image',
            isFluidWidth: false,
            src: data_url,
            width: smallImage?.width || 400,
            height: smallImage?.height || 500,
        },
        largeImage: {
            src: data_url,
            width: largeImage?.width || 1200,
            height: largeImage?.height || 1800,
        },
        enlargedImageClassName: 'large_image',
        enlargedImageContainerDimensions: {
            width: '200%',
            height: '100%',
        },
    };
    return <ReactImageMagnify {...defaultProps} />;
};

ImageMagnify.propTypes = {
    data_url: PropTypes.string.isRequired, // The source URL of the image
    imageClassName: PropTypes.string, // CSS class for the small image
    smallImage: PropTypes.shape({
        alt: PropTypes.string,
        isFluidWidth: PropTypes.bool,
        src: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
    }),
    largeImage: PropTypes.shape({
        src: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
    }),
    enlargedImageClassName: PropTypes.string, // CSS class for the enlarged image
    enlargedImageContainerDimensions: PropTypes.shape({
        width: PropTypes.string,
        height: PropTypes.string,
    }),
};

export default ImageMagnify;
